import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { User, Lock, Mail, Save, Loader2, Sun, Moon } from 'lucide-react';
import { toast } from 'sonner';
import { getTheme, setTheme as applyThemeChoice } from '../lib/theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Settings = () => {
  const { user, setUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [theme, setThemeState] = useState(getTheme());
  const [formData, setFormData] = useState({
    email: user?.email || '',
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  const handleThemeChange = (choice) => {
    applyThemeChoice(choice);
    setThemeState(choice);
    toast.success(choice === 'dark' ? 'Mode sombre activé' : 'Mode clair activé');
  };

  const handleEmailChange = async (e) => {
    e.preventDefault();
    
    if (!formData.email.trim()) {
      toast.error('L\'email est obligatoire');
      return;
    }
    
    if (!formData.email.includes('@')) {
      toast.error('Email invalide');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API}/users/me/email`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: formData.email }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setUser(prev => ({ ...prev, email: formData.email }));
        toast.success('Email mis a jour avec succes');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la mise a jour');
      }
    } catch (error) {
      console.error('Email update error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (!formData.current_password) {
      toast.error('Mot de passe actuel requis');
      return;
    }
    
    if (!formData.new_password) {
      toast.error('Nouveau mot de passe requis');
      return;
    }
    
    if (formData.new_password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caracteres');
      return;
    }
    
    if (formData.new_password !== formData.confirm_password) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API}/users/me/password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          current_password: formData.current_password,
          new_password: formData.new_password 
        }),
      });
      
      if (response.ok) {
        toast.success('Mot de passe mis a jour avec succes');
        setFormData(prev => ({
          ...prev,
          current_password: '',
          new_password: '',
          confirm_password: '',
        }));
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la mise a jour');
      }
    } catch (error) {
      console.error('Password update error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-[Manrope]" data-testid="settings-title">
          Parametres
        </h1>
        <p className="text-slate-500 mt-1">Gerez vos informations de compte</p>
      </div>

      {/* Appearance / Theme */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-violet-100">
              <Moon className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <CardTitle className="text-lg">Apparence</CardTitle>
              <CardDescription>Choisissez le thème de l'interface (mémorisé sur cet appareil)</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3 max-w-sm">
            <button
              type="button"
              onClick={() => handleThemeChange('light')}
              className={`flex items-center justify-center gap-2 py-3 rounded-xl border-2 transition-colors ${theme === 'light' ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}
              data-testid="theme-light-btn"
            >
              <Sun className="w-4 h-4" /> Clair
            </button>
            <button
              type="button"
              onClick={() => handleThemeChange('dark')}
              className={`flex items-center justify-center gap-2 py-3 rounded-xl border-2 transition-colors ${theme === 'dark' ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}
              data-testid="theme-dark-btn"
            >
              <Moon className="w-4 h-4" /> Sombre
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Profile info */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-indigo-100">
              <User className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <CardTitle className="text-lg">Informations du compte</CardTitle>
              <CardDescription>Votre identifiant de connexion</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleEmailChange} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email (identifiant)</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="pl-10"
                    placeholder="votre@email.com"
                    data-testid="email-input"
                  />
                </div>
                <Button type="submit" disabled={loading} data-testid="save-email-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Password change */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-100">
              <Lock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <CardTitle className="text-lg">Changer le mot de passe</CardTitle>
              <CardDescription>Securisez votre compte</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current_password">Mot de passe actuel</Label>
              <Input
                id="current_password"
                type="password"
                value={formData.current_password}
                onChange={(e) => setFormData(prev => ({ ...prev, current_password: e.target.value }))}
                placeholder="••••••••"
                data-testid="current-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new_password">Nouveau mot de passe</Label>
              <Input
                id="new_password"
                type="password"
                value={formData.new_password}
                onChange={(e) => setFormData(prev => ({ ...prev, new_password: e.target.value }))}
                placeholder="••••••••"
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirmer le mot de passe</Label>
              <Input
                id="confirm_password"
                type="password"
                value={formData.confirm_password}
                onChange={(e) => setFormData(prev => ({ ...prev, confirm_password: e.target.value }))}
                placeholder="••••••••"
                data-testid="confirm-password-input"
              />
            </div>
            <Button type="submit" disabled={loading} className="w-full" data-testid="save-password-btn">
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Mise a jour...
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4 mr-2" />
                  Changer le mot de passe
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Account info */}
      <Card className="border-0 shadow-sm bg-slate-50">
        <CardContent className="p-6">
          <div className="text-sm text-slate-500 space-y-1">
            <p><span className="font-medium">Nom:</span> {user?.name}</p>
            <p><span className="font-medium">Role:</span> {user?.role === 'admin' ? 'Administrateur' : 'Commercial'}</p>
            <p><span className="font-medium">ID:</span> {user?.user_id}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
